// Ops · Zones & fees: drawing a zone point by point, the zone list and the delivery settings
// (docs/PHASE7-CONTRACT.md, contract.d.ts "Ops · Zones & fees"). Pure: node --test runs it.
// Coordinates are [lng, lat] like GeoJSON and the API's ZoneInput.polygon.

/** @typedef {import('@popcart/core/v1').Zone} Zone */
/** @typedef {import('@popcart/core/v1').ZoneInput} ZoneInput */
/** @typedef {import('@popcart/core/v1').PopCartApi} PopCartApi */
/** @typedef {[number, number]} LngLat */

export const zoneKeys = { all: ['ops', 'zones'] };

/** Where the map opens with no zones yet: the first PopCart hub (Quezon City, seed-dev). */
export const DEFAULT_CENTER = /** @type {LngLat} */ ([121.03, 14.6507]);
export const DEFAULT_ZOOM = 13;

export const ZONE_NAME_MAX = 60;
export const PUROK_MAX = 60;

/** Round to ~1 cm so saved rings stay small and stable. @param {LngLat} p @returns {LngLat} */
export function roundPoint(p) {
  return [Math.round(p[0] * 1e7) / 1e7, Math.round(p[1] * 1e7) / 1e7];
}

/** Add a clicked point. @param {readonly LngLat[]} pts @param {LngLat} p @returns {LngLat[]} */
export function addPoint(pts, p) {
  return [...pts, roundPoint(p)];
}

/** Undo the last point. @param {readonly LngLat[]} pts @returns {LngLat[]} */
export function undoPoint(pts) {
  return pts.slice(0, -1);
}

/** The ring without a repeated closing point (the API takes either). @param {readonly LngLat[]} ring @returns {LngLat[]} */
export function openRing(ring) {
  if (ring.length > 1) {
    const a = ring[0];
    const b = ring[ring.length - 1];
    if (a && b && a[0] === b[0] && a[1] === b[1]) return ring.slice(0, -1);
  }
  return [...ring];
}

/** The ring closed (first point repeated), for drawing. @param {readonly LngLat[]} pts @returns {LngLat[]} */
export function closedRing(pts) {
  const open = openRing(pts);
  const first = open[0];
  return first && open.length >= 3 ? [...open, first] : open;
}

/**
 * Area in km² (spherical excess on the WGS84 mean radius, good to well under 1% at barangay scale).
 * Lets the editor show the size before the API's own areaKm2 comes back.
 * @param {readonly LngLat[]} pts
 */
export function areaKm2(pts) {
  const ring = openRing(pts);
  if (ring.length < 3) return 0;
  const R = 6371.0088;
  const rad = Math.PI / 180;
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const p1 = /** @type {LngLat} */ (ring[i]);
    const p2 = /** @type {LngLat} */ (ring[(i + 1) % ring.length]);
    sum += (p2[0] - p1[0]) * rad * (2 + Math.sin(p1[1] * rad) + Math.sin(p2[1] * rad));
  }
  return Math.abs((sum * R * R) / 2);
}

/** "0.84 km²", "12.3 km²". @param {number} km2 */
export function areaLabel(km2) {
  if (!Number.isFinite(km2) || km2 <= 0) return '—';
  return `${km2 < 10 ? Math.round(km2 * 100) / 100 : Math.round(km2 * 10) / 10} km²`;
}

/**
 * Do two segments properly cross (shared endpoints don't count)?
 * @param {LngLat} a @param {LngLat} b @param {LngLat} c @param {LngLat} d
 */
function crosses(a, b, c, d) {
  const o = (/** @type {LngLat} */ p, /** @type {LngLat} */ q, /** @type {LngLat} */ r) => Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]));
  return o(a, b, c) * o(a, b, d) < 0 && o(c, d, a) * o(c, d, b) < 0;
}

/** Does the ring cross itself (a figure-8)? @param {readonly LngLat[]} pts */
export function selfIntersects(pts) {
  const r = openRing(pts);
  const n = r.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (Math.abs(i - j) <= 1 || (i === 0 && j === n - 1)) continue;
      if (crosses(/** @type {LngLat} */ (r[i]), /** @type {LngLat} */ (r[(i + 1) % n]), /** @type {LngLat} */ (r[j]), /** @type {LngLat} */ (r[(j + 1) % n]))) return true;
    }
  }
  return false;
}

/**
 * Validate the editor into a ZoneInput (or a patch for an existing zone).
 * @param {{ name: string; purok: string; points: readonly LngLat[] }} draft
 * @returns {{ ok: true; value: ZoneInput } | { ok: false; errors: { name?: string; purok?: string; points?: string } }}
 */
export function validateZone(draft) {
  /** @type {{ name?: string; purok?: string; points?: string }} */
  const errors = {};
  const name = String(draft.name ?? '').trim();
  const purok = String(draft.purok ?? '').trim();
  const pts = openRing(draft.points);
  if (name.length < 2) errors.name = 'Name the zone, like “Brgy. Pinyahan”.';
  else if (name.length > ZONE_NAME_MAX) errors.name = `Keep it under ${ZONE_NAME_MAX} characters.`;
  if (purok.length > PUROK_MAX) errors.purok = `Keep it under ${PUROK_MAX} characters.`;
  if (pts.length < 3) errors.points = `Click at least 3 points on the map (${pts.length} so far).`;
  else if (selfIntersects(pts)) errors.points = 'The edges cross. Undo points until the shape is a clean loop.';
  if (errors.name || errors.purok || errors.points) return { ok: false, errors };
  return { ok: true, value: { name, purok: purok || null, polygon: pts.map(roundPoint) } };
}

/**
 * Save the editor: create a zone, or update the one being edited.
 * @param {Pick<PopCartApi, 'createZone' | 'updateZone'>} api
 * @param {string | null} zoneId
 * @param {{ name: string; purok: string; points: readonly LngLat[] }} draft
 */
export async function saveZone(api, zoneId, draft) {
  const r = validateZone(draft);
  if (!r.ok) return r;
  const zone = zoneId ? await api.updateZone(zoneId, r.value) : await api.createZone({ ...r.value, isActive: true });
  return { ok: /** @type {const} */ (true), zone };
}

/**
 * [[minLng, minLat], [maxLng, maxLat]] around these rings, or null with nothing to frame.
 * @param {readonly (readonly LngLat[])[]} rings
 * @returns {[LngLat, LngLat] | null}
 */
export function boundsOf(rings) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const ring of rings) {
    for (const [x, y] of ring) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }
  return Number.isFinite(minX) ? [[minX, minY], [maxX, maxY]] : null;
}

/**
 * Saved zones as GeoJSON for the map (the one being edited is left out: the draft draws it).
 * @param {readonly Zone[]} zones
 * @param {string | null} [skipId]
 */
export function zonesGeoJSON(zones, skipId = null) {
  return {
    type: /** @type {const} */ ('FeatureCollection'),
    features: zones
      .filter((z) => z.id !== skipId && openRing(z.polygon).length >= 3)
      .map((z) => ({
        type: /** @type {const} */ ('Feature'),
        id: z.id,
        properties: { id: z.id, name: z.name, active: z.isActive },
        geometry: { type: /** @type {const} */ ('Polygon'), coordinates: [closedRing(z.polygon)] },
      })),
  };
}

/**
 * The draft as GeoJSON: its points, the outline so far and, from 3 points, the filled shape.
 * @param {readonly LngLat[]} pts
 */
export function draftGeoJSON(pts) {
  /** @type {any[]} */
  const features = pts.map((p, i) => ({ type: 'Feature', properties: { kind: 'point', first: i === 0 }, geometry: { type: 'Point', coordinates: p } }));
  if (pts.length >= 2) features.push({ type: 'Feature', properties: { kind: 'line' }, geometry: { type: 'LineString', coordinates: closedRing(pts) } });
  if (pts.length >= 3) features.push({ type: 'Feature', properties: { kind: 'fill' }, geometry: { type: 'Polygon', coordinates: [closedRing(pts)] } });
  return { type: /** @type {const} */ ('FeatureCollection'), features };
}

/** Active first, then by name. @param {readonly Zone[]} zones */
export function sortZones(zones) {
  return [...zones].sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name));
}

/**
 * What enforceZones does right now, in words.
 * @param {boolean} enforce
 * @param {readonly Zone[]} zones
 */
export function enforcementNote(enforce, zones) {
  const active = zones.filter((z) => z.isActive).length;
  if (!enforce) return 'Off: anyone within the delivery distance can order. Zones are drawn but not checked.';
  if (!active) return 'On, but no zone is active, so nothing is checked yet. Turn a zone on to start.';
  return `On: addresses outside the ${active === 1 ? 'active zone' : `${active} active zones`} can't order.`;
}

/**
 * The delivery fee as a ladder: one step per whole km up to the delivery limit, using the quote
 * engine's rule (base + every started km past the included distance; @popcart/core plan.js
 * deliveryFee). Returns [] when the numbers aren't usable yet (a half-typed draft).
 * @param {Record<string, unknown> | null | undefined} d delivery settings (wire values: centavos, km)
 * @returns {{ km: number; feeCentavos: number; extraCentavos: number; included: boolean }[]}
 */
export function feeLadder(d) {
  const base = Number(d?.baseFeeCentavos);
  const inc = Number(d?.includedKm);
  const per = Number(d?.perKmCentavos);
  const max = Number(d?.maxKm);
  if (![base, inc, per, max].every(Number.isFinite) || max <= 0) return [];
  const steps = Math.min(30, Math.ceil(max - 1e-9));
  const out = [];
  for (let km = 1; km <= steps; km++) {
    const extraKm = Math.max(0, Math.ceil(km - inc - 1e-9));
    out.push({ km, feeCentavos: base + extraKm * per, extraCentavos: extraKm * per, included: km <= inc + 1e-9 });
  }
  return out;
}
